import {
  createUnifiedTheme,
  genPageTheme,
  palettes,
} from '@backstage/theme';

const fontFamily =
  'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const pageThemeIds = [
  'home',
  'documentation',
  'tool',
  'service',
  'website',
  'library',
  'other',
  'app',
  'apis',
  'card',
] as const;

function flatPageThemes(background: string, fontColor: string) {
  const theme = genPageTheme({
    colors: [background, background],
    shape: 'none',
    options: { fontColor },
  });
  return Object.fromEntries(pageThemeIds.map(id => [id, theme]));
}

const typography = {
  htmlFontSize: 16,
  fontFamily,
  h1: { fontSize: '1.875rem', fontWeight: 600, marginBottom: 8 },
  h2: { fontSize: '1.375rem', fontWeight: 600, marginBottom: 12 },
  h3: { fontSize: '1.125rem', fontWeight: 600, marginBottom: 8 },
  h4: { fontSize: '1rem', fontWeight: 600, marginBottom: 8 },
  h5: { fontSize: '0.9375rem', fontWeight: 600, marginBottom: 6 },
  h6: { fontSize: '0.875rem', fontWeight: 600, marginBottom: 6 },
};

const components = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 6,
        textTransform: 'none' as const,
        fontWeight: 500,
        boxShadow: 'none',
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      rounded: {
        borderRadius: 12,
      },
      elevation1: {
        boxShadow: 'none',
      },
      elevation2: {
        boxShadow: 'none',
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 6,
      },
    },
  },
};

function paletteExtras(colors: {
  background: string;
  paper: string;
  text: string;
  textSecondary: string;
  textTertiary: string;
  border: string;
  primary: string;
  primaryHover: string;
  success: string;
  warning: string;
  danger: string;
  info: string;
  infoBackground: string;
  errorBackground: string;
  warningBackground: string;
  hover: string;
}) {
  return {
    background: {
      default: colors.background,
      paper: colors.paper,
    },
    primary: {
      main: colors.primary,
      dark: colors.primaryHover,
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.primary,
      contrastText: '#ffffff',
    },
    error: { main: colors.danger },
    warning: { main: colors.warning },
    success: { main: colors.success },
    info: { main: colors.info },
    divider: colors.border,
    text: {
      primary: colors.text,
      secondary: colors.textSecondary,
      disabled: colors.textTertiary,
    },
    border: colors.border,
    textContrast: colors.text,
    textVerySubtle: colors.textTertiary,
    textSubtle: colors.textSecondary,
    highlight: colors.warningBackground,
    errorBackground: colors.errorBackground,
    warningBackground: colors.warningBackground,
    infoBackground: colors.infoBackground,
    errorText: colors.danger,
    infoText: colors.primary,
    warningText: colors.warning,
    linkHover: colors.primaryHover,
    link: colors.primary,
    navigation: {
      background: colors.paper,
      indicator: colors.primary,
      color: colors.textSecondary,
      selectedColor: colors.primary,
      navItem: {
        hoverBackground: colors.hover,
      },
      submenu: {
        background: colors.hover,
      },
    },
    pinSidebarButton: {
      icon: colors.textSecondary,
      background: colors.hover,
    },
    tabbar: {
      indicator: colors.primary,
    },
    banner: {
      info: colors.primary,
      error: colors.danger,
      text: '#ffffff',
      link: colors.primaryHover,
      closeButtonColor: '#ffffff',
      warning: colors.warning,
    },
    bursts: {
      fontColor: colors.text,
      slackChannelText: colors.textSecondary,
      backgroundColor: {
        default: colors.paper,
      },
      gradient: {
        linear: `linear-gradient(90deg, ${colors.background}, ${colors.background})`,
      },
    },
    code: {
      background: colors.paper,
    },
  };
}

const lightColors = {
  background: '#ffffff',
  paper: '#f8fafc',
  text: '#0f172a',
  textSecondary: '#475569',
  textTertiary: '#64748b',
  border: '#e2e8f0',
  primary: '#3b82f6',
  primaryHover: '#2563eb',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  infoBackground: '#dbeafe',
  errorBackground: '#fef2f2',
  warningBackground: '#fffbeb',
  hover: '#f1f5f9',
};

const darkColors = {
  background: '#0d1117',
  paper: '#161b22',
  text: '#e6edf3',
  textSecondary: '#8b949e',
  textTertiary: '#6e7681',
  border: '#30363d',
  primary: '#4493e6',
  primaryHover: '#58a6ff',
  success: '#34d399',
  warning: '#fbbf24',
  danger: '#f87171',
  info: '#58a6ff',
  infoBackground: '#13233a',
  errorBackground: '#3f1d24',
  warningBackground: '#3b2f14',
  hover: '#21262d',
};

export const fluxLightTheme = createUnifiedTheme({
  fontFamily,
  htmlFontSize: 16,
  defaultPageTheme: 'home',
  pageTheme: flatPageThemes(lightColors.background, lightColors.text),
  palette: {
    ...palettes.light,
    ...paletteExtras(lightColors),
  },
  typography,
  components,
});

export const fluxDarkTheme = createUnifiedTheme({
  fontFamily,
  htmlFontSize: 16,
  defaultPageTheme: 'home',
  pageTheme: flatPageThemes(darkColors.background, darkColors.text),
  palette: {
    ...palettes.dark,
    ...paletteExtras(darkColors),
  },
  typography,
  components,
});
